import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useAuthStore } from '../../../auth/authStore';
import { BookingFlowShell, type BookingFlowShellConfig, type BookingStepContext } from '../../../booking-flow/BookingFlowShell';
import { airFlowConfig, isFirstBookingReservable, type AirFlowState } from '../flowConfig';
import { airResumeAdapter } from '../resume';
import { parseLegacyDateTime } from '../paymentResume';
import type { AirBooking, OriginDestinationOption, SelectableFare } from '../../../api/types/air';
import { AirSearchForm } from '../components/AirSearchForm';
import { AirResultsList } from '../components/AirResultsList';
import { AirUpsellPanel } from '../components/AirUpsellPanel';
import { AirInsurancePanel } from '../components/AirInsurancePanel';
import { AirPassengerForm } from '../components/AirPassengerForm';
import { AirApprovalPassengerStep } from '../components/AirApprovalPassengerStep';
import { approvalEmployeeId, corporateMaxBudget, needsCorporateApproval } from '../corporateApproval';
import { PaymentForm } from '../../../components/payment/PaymentForm';
import { SendForApprovalPanel } from '../../../components/booking/SendForApprovalPanel';
import { FinalizeRetryPanel } from '../../../components/booking/FinalizeRetryPanel';
import { AirSeatSelectionStep } from '../components/AirSeatSelectionStep';

/** Allocate adımı — sonuç listesi + (tek yön / uluslararası gidiş-dönüş yolunda) bir uçuş
 *  seçildikten sonra branded-fare Upsell ara paneli. Split ve çok noktalı yollar doğrudan
 *  Allocate'e gider. Panel state'ini bu adıma yerel tutmak için burada. */
function AirAllocateStep({
  state,
  running,
  onRun,
}: {
  state: AirFlowState;
  running: boolean;
  onRun: (partial: Partial<AirFlowState>) => void;
}) {
  const [pending, setPending] = useState<{ option: OriginDestinationOption; fare: SelectableFare } | null>(null);

  if (pending) {
    return (
      <AirUpsellPanel
        option={pending.option}
        fare={pending.fare}
        loading={running}
        onBack={() => setPending(null)}
        onContinue={() => onRun({ selectedOption: pending.option, selectedFare: pending.fare })}
        onSelectUpsell={(option, fare) => onRun({ selectedOption: option, selectedFare: fare })}
      />
    );
  }

  return (
    <AirResultsList
      result={state.searchResult}
      loading={running}
      onSelect={(option, fare) => setPending({ option, fare })}
      onSelectRoundTrip={(departure, ret) =>
        onRun({
          selectedOption: departure.option,
          selectedFare: departure.fare,
          selectedReturnOption: ret.option,
          selectedReturnFare: ret.fare,
        })
      }
      onSelectMultiCity={(selections) => onRun({ multiCitySelections: selections })}
    />
  );
}

function airFirstBooking(state: AirFlowState): AirBooking | undefined {
  return (state.shoppingCart?.airBookings as AirBooking[] | undefined)?.[0];
}

function airIsPaymentDone(state: AirFlowState): boolean {
  return !!state.paymentResult || !!state.shoppingCart?.isFinalized;
}

function PassengersStep({ ctx }: { ctx: BookingStepContext<AirFlowState> }) {
  const { flow, running, runStep } = ctx;
  const sessionEmployeeId = useAuthStore((s) => s.employeeId);
  const firstBooking = airFirstBooking(flow.state);
  const isInternational = firstBooking?.isInternational ?? flow.state.searchResult?.isInternational ?? false;

  const approvalEmpId = approvalEmployeeId(flow.state, sessionEmployeeId);
  if (needsCorporateApproval(flow.state) && approvalEmpId) {
    return (
      <AirApprovalPassengerStep
        employeeId={approvalEmpId}
        isInternational={isInternational}
        maxBudget={corporateMaxBudget(flow.state)}
        productLabel="uçuş"
        loading={running}
        onSubmit={(passengers, contact, billingInformation) =>
          runStep({ passengers, contact, billingInformation, sentForApproval: true })
        }
      />
    );
  }
  return (
    <AirPassengerForm
      paxCounts={(flow.state.searchRequest?.airSearchPaxItems ?? []).map((p) => ({
        paxType: p.paxCode ?? 'ADT',
        count: p.paxCount ?? 1,
      }))}
      loading={running}
      isInternational={isInternational}
      provider={firstBooking?.provider}
      isReservable={isFirstBookingReservable(flow.state.shoppingCart)}
      onSubmit={(passengers, contact, currentAccount, billingInformation) =>
        runStep({ passengers, contact, selectedCurrentAccount: currentAccount, billingInformation })
      }
    />
  );
}

function makeAirShellConfig(
  urlResume: { cartId: string; deadlineMs: number | null } | undefined,
  navigate: ReturnType<typeof useNavigate>,
  enqueueSnackbar: ReturnType<typeof useSnackbar>['enqueueSnackbar'],
  currentUser: ReturnType<typeof useAuthStore.getState>['currentUser']
): BookingFlowShellConfig<AirFlowState> {
  return {
    title: 'Uçak Rezervasyonu',
    description:
      'Arama (tek yön / gidiş-dönüş / çok noktalı) → Uçuş Seçimi → Yolcu Bilgisi → Koltuk Seçimi (THYNDC) → Ödeme → Kesinleştirme',
    flowConfig: airFlowConfig,
    resumeAdapter: airResumeAdapter,
    urlResume,
    deadline: {
      getDeadlineMs: (state, resumeDeadlineMs) => resumeDeadlineMs ?? state.searchDeadline ?? null,
      isPaymentDone: (state) => !!state.paymentResult,
      tabTitlePrefix: 'Uçak Rezervasyonu',
      expiredCopy: {
        resumed: 'Bu rezervasyonun son geçerlilik süresi doldu.',
        fresh: 'Arama sonrası tanınan 15 dakikalık süre doldu.',
      },
    },
    sentForApproval: {
      isSent: (s) => !!s.sentForApproval,
      passengerStepId: 'passengers',
      reservationPath: '/air/reservations',
      message:
        'Uçuş onaya gönderilmiştir. Yöneticiniz onayladığında rezervasyon listenizden ödemeye devam edebilirsiniz.',
    },
    renderStep: {
      search: ({ runStep }) => (
        <AirSearchForm loading={false} onSearch={(request) => runStep({ searchRequest: request })} />
      ),
      allocate: ({ flow, running, runStep }) => (
        <AirAllocateStep state={flow.state} running={running} onRun={(partial) => runStep(partial)} />
      ),
      passengers: (ctx) => <PassengersStep ctx={ctx} />,
      seatSelection: ({ flow, running, runStep }) => (
        <AirSeatSelectionStep
          shoppingCart={flow.state.shoppingCart}
          loading={running}
          onContinue={() => runStep()}
          onShoppingCartChange={(shoppingCart) => flow.patchState({ shoppingCart })}
        />
      ),
      payment: ({ flow, running, runStep }) => (
        <>
          <SendForApprovalPanel product="Flight" cart={flow.state.shoppingCart} />
          <AirInsurancePanel
            cart={flow.state.shoppingCart}
            wants={!!flow.state.wantsInsurance}
            onChange={(w) => flow.patchState({ wantsInsurance: w })}
          />
          <PaymentForm
            amount={flow.state.shoppingCart?.remainingTotal}
            currency="TRY"
            loading={running}
            currentAccount={flow.state.selectedCurrentAccount}
            shoppingCartId={flow.state.shoppingCart?.id}
            creditCardPaymentAllowed={currentUser?.creditCardPayment ?? true}
            threeDPaymentAllowed={!!currentUser?.threeDPayment}
            onSubmit={({ paymentForm, paymentType }) => runStep({ paymentForm, paymentType })}
          />
        </>
      ),
      finalize: ({ flow, running, runStep }) => {
        if (airIsPaymentDone(flow.state) && flow.state.shoppingCart?.isFinalized) {
          return (
            <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                Rezervasyonunuz tamamlandı, bilet detay sayfanıza yönlendiriliyorsunuz…
              </Typography>
            </Box>
          );
        }
        return (
          <FinalizeRetryPanel
            paymentTaken={!!flow.state.paymentResult}
            running={running}
            onRetry={() => runStep()}
          />
        );
      },
    },
    onComplete: ({ flow }) => {
      if (flow.state.insuranceError) {
        enqueueSnackbar(`Sigorta poliçesi oluşturulamadı: ${flow.state.insuranceError}`, { variant: 'warning' });
      }
      const firstBooking = airFirstBooking(flow.state);
      const pnr = firstBooking?.bookingCode;
      const ticket = firstBooking?.passengerBreakdowns?.find((pb) => pb.ticketNumber)?.ticketNumber;
      const target = pnr
        ? `/air/reservations/${encodeURIComponent(pnr)}?by=pnr`
        : ticket
          ? `/air/reservations/${encodeURIComponent(ticket)}?by=ticket`
          : '/air/reservations';
      flow.reset();
      navigate(target, { replace: true });
    },
  };
}

/**
 * `?resume=<shoppingCartId>&expireAt=<DD.MM.YYYY HH:mm:ss>` — rezervasyon listesi/detay
 * sayfasındaki "Ödemeye Git" bağlantısının hedefi. Diğer akış-özel mantık (süre geri sayımı,
 * kurumsal onay, akış sonu yönlendirme) artık `BookingFlowShell` config'inde.
 */
export default function AirFlowPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const currentUser = useAuthStore((s) => s.currentUser);

  const resumeId = searchParams.get('resume');
  const resumeExpireAt = searchParams.get('expireAt');
  const urlResume = resumeId
    ? { cartId: resumeId, deadlineMs: parseLegacyDateTime(resumeExpireAt) }
    : undefined;

  const config = makeAirShellConfig(urlResume, navigate, enqueueSnackbar, currentUser);
  return <BookingFlowShell config={config} />;
}
